import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

interface VendorHoursUpdate {
  vendorId: string;
  vendorName?: string;
  deliveryHours: any;
  updatedAt: string;
}

interface UseVendorHoursRealtimeOptions {
  /** Array of vendor IDs to monitor for delivery_hours changes */
  vendorIds: string[];
  /** Callback when any vendor's delivery_hours change */
  onHoursChange?: (update: VendorHoursUpdate) => void;
  /** Whether to enable the subscription (default: true) */
  enabled?: boolean;
}

/**
 * Hook to subscribe to real-time vendor delivery_hours changes.
 * Used in CartScreen to block checkout when a store closes or changes hours.
 * 
 * When a vendor updates their store hours while a customer is viewing
 * their cart, this hook will notify the CartScreen to re-validate
 * store hours and potentially block checkout.
 */
export function useVendorHoursRealtime({
  vendorIds,
  onHoursChange,
  enabled = true,
}: UseVendorHoursRealtimeOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [lastUpdate, setLastUpdate] = useState<VendorHoursUpdate | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Use refs to track current values for callbacks
  const vendorIdsRef = useRef(vendorIds);
  const onHoursChangeRef = useRef(onHoursChange);
  
  useEffect(() => {
    vendorIdsRef.current = vendorIds;
  }, [vendorIds]);
  
  useEffect(() => {
    onHoursChangeRef.current = onHoursChange;
  }, [onHoursChange]);

  const setupSubscription = useCallback(() => {
    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    const currentVendorIds = vendorIdsRef.current;
    
    if (!currentVendorIds.length) {
      logger.debug('VendorHoursRealtime', 'No vendor IDs to monitor');
      return;
    }
    
    logger.debug('VendorHoursRealtime', `Setting up realtime subscription for ${currentVendorIds.length} vendor(s)`, {
      vendorIds: currentVendorIds,
    });

    // Create a unique channel name based on vendor IDs
    const channelName = `vendor-hours-${currentVendorIds.slice(0, 3).join('-')}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'vendors',
        },
        (payload) => {
          const newRow = payload.new as any;
          const oldRow = payload.old as any;
          
          // Only process if this is a vendor we're monitoring
          if (!currentVendorIds.includes(newRow?.id)) {
            return;
          }
          
          // IMPORTANT: Supabase Realtime by default only sends primary key in oldRow
          // unless REPLICA IDENTITY FULL is set on the table. If oldRow doesn't have
          // delivery_hours, we cannot determine if hours actually changed - so skip.
          // This prevents false positives on every vendor update.
          if (!oldRow || !('delivery_hours' in oldRow)) {
            logger.debug('VendorHoursRealtime', 'Vendor updated but old delivery_hours not available (replica identity not FULL)', {
              vendorId: newRow?.id,
              oldRowKeys: Object.keys(oldRow || {}),
            });
            return;
          }
          
          // Check if delivery_hours actually changed
          const oldHours = JSON.stringify(oldRow.delivery_hours);
          const newHours = JSON.stringify(newRow?.delivery_hours);
          
          if (oldHours === newHours) {
            logger.debug('VendorHoursRealtime', 'Vendor updated but delivery_hours unchanged', {
              vendorId: newRow?.id,
            });
            return;
          }
          
          logger.debug('VendorHoursRealtime', '🕒 Vendor delivery_hours changed!', {
            vendorId: newRow?.id,
            vendorName: newRow?.store_name,
            oldHours: oldRow?.delivery_hours,
            newHours: newRow?.delivery_hours,
          });
          
          const update: VendorHoursUpdate = {
            vendorId: newRow.id,
            vendorName: newRow.store_name,
            deliveryHours: newRow.delivery_hours,
            updatedAt: newRow.updated_at || new Date().toISOString(),
          };
          
          setLastUpdate(update);
          
          // Call the change handler if provided
          if (onHoursChangeRef.current) {
            onHoursChangeRef.current(update);
          }
        }
      )
      .subscribe((status) => {
        const statusStr = typeof status === 'string' ? status : String(status || 'UNKNOWN');
        logger.debug('VendorHoursRealtime', `Subscription status: ${statusStr}`);
        
        if (statusStr === 'SUBSCRIBED') {
          logger.debug('VendorHoursRealtime', '✅ Successfully subscribed to vendor hours changes');
          setIsConnected(true);
          
          // Clear any pending reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        } else if (statusStr === 'CHANNEL_ERROR' || statusStr === 'TIMED_OUT') {
          logger.error('VendorHoursRealtime', `❌ Subscription error: ${statusStr}`);
          setIsConnected(false);
        } else if (statusStr === 'CLOSED') {
          logger.debug('VendorHoursRealtime', 'Subscription closed');
          setIsConnected(false);
          
          // Attempt to reconnect after a delay
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            logger.debug('VendorHoursRealtime', '🔄 Attempting to reconnect...');
            setupSubscription();
          }, 3000);
        }
      });
    
    channelRef.current = channel;
  }, []); // Empty deps - uses refs for values

  useEffect(() => {
    if (!enabled || vendorIds.length === 0) {
      // Clean up if disabled or no vendors
      if (channelRef.current) {
        logger.debug('VendorHoursRealtime', 'Cleaning up subscription (disabled or no vendors)');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsConnected(false);
      return;
    }
    
    setupSubscription();
    
    return () => {
      logger.debug('VendorHoursRealtime', 'Cleaning up subscription (unmount)');
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      
      setIsConnected(false);
    };
  }, [enabled, vendorIds.join(','), setupSubscription]);

  return {
    /** The most recent delivery_hours update received */
    lastUpdate,
    /** Whether the realtime connection is active */
    isConnected,
  };
}

